import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Plus, Search, Upload, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { ProjectStats } from "@/components/project/project-stats";
import { ProjectList } from "@/components/project/project-list";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileDashboard } from "./mobile/mobile-dashboard";
import { NoApiKeyBanner } from "@/components/NoApiKeyBanner";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { projectExportSchema } from "@shared/schema";

export default function DashboardPage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);
  const importAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => { importAbortRef.current?.abort(); };
  }, []);

  if (isMobile) {
    return <MobileDashboard />;
  }

  const handleImportFile = async (file: File) => {
    if (!file.name.endsWith(".json")) {
      toast({ title: "Ungültige Datei", description: "Bitte eine .json-Exportdatei auswählen.", variant: "destructive" });
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast({ title: "Datei zu groß", description: "Die Exportdatei darf maximal 50 MB groß sein.", variant: "destructive" });
      return;
    }
    setImporting(true);
    toast({ title: "Projekt wird importiert …", description: "Bitte warten." });
    const abortCtrl = new AbortController();
    importAbortRef.current = abortCtrl;
    try {
      const text = await file.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error("Die Datei enthält kein gültiges JSON.");
      }
      const validated = projectExportSchema.safeParse(parsed);
      if (!validated.success) {
        const firstIssue = validated.error.issues[0];
        if (firstIssue?.path?.includes("schemaVersion")) {
          throw new Error("Ungültiges Exportformat. Bitte eine gültige FassadenCheck-Exportdatei verwenden.");
        }
        throw new Error(`Ungültige Exportdatei: ${firstIssue?.message ?? "Unbekanntes Format"}`);
      }
      const res = await apiRequest("POST", "/api/projects/import", validated.data, abortCtrl.signal);
      const body = await res.json() as { projectId?: number };
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/search"] });
      toast({
        title: "Projekt importiert",
        description: `„${validated.data.project.name}" wurde erfolgreich importiert.`,
      });
      if (body.projectId) {
        navigate(`/project/edit/${body.projectId}`);
      }
    } catch (err) {
      if ((err as { name?: string }).name === "AbortError") return;
      toast({
        title: "Import fehlgeschlagen",
        description: err instanceof Error ? err.message : "Unbekannter Fehler",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
      importAbortRef.current = null;
    }
  };

  const actionCards = [
    {
      icon: Plus,
      title: "Neues Projekt",
      description: "Neue Erfassung starten",
      onClick: () => navigate("/project/create"),
    },
    {
      icon: Search,
      title: "Projekte suchen",
      description: "Bestehende Projekte finden",
      onClick: () => navigate("/search"),
    },
    {
      icon: Upload,
      title: "Projekt importieren",
      description: "Exportdatei hochladen",
      onClick: () => importInputRef.current?.click(),
      loading: importing,
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <input
        ref={importInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleImportFile(file);
          e.target.value = "";
        }}
      />

      <div className="mb-8">
          <h1 className="text-4xl font-bold text-bauwerk-teal mb-2">Dashboard</h1>
          <p className="text-gray-600">Übersicht Ihrer Projekte und Aktivitäten</p>
      </div>

      <NoApiKeyBanner />

      <ProjectStats />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {actionCards.map((card) => (
            <Card
              key={card.title}
              className={`cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all duration-200 ${card.loading ? "opacity-70 pointer-events-none" : ""}`}
              onClick={card.onClick}
            >
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-bauwerk-teal rounded-full flex items-center justify-center mb-4">
                  {card.loading ? (
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  ) : (
                    <card.icon className="w-6 h-6 text-white" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-bauwerk-teal mb-2">
                  {card.title}
                </h3>
                <p className="text-gray-600 text-sm">{card.description}</p>
              </CardContent>
            </Card>
          ))}
      </div>

      <ProjectList />
    </div>
  );
}
